import { useCallback, useEffect, useRef, useState } from "react";

import { detectCodec } from "./codecs";
import { type CapturedMedia, generateFilename } from "./utils";

// Mapped from the native MediaRecorder error's DOMException name.
export type RecordingErrorKind =
  | "unsupported"
  | "size_exceeded"
  | "duration_exceeded"
  | "invalid_state" // InvalidStateError
  | "security" // SecurityError
  | "track_changed" // InvalidModificationError
  | "encoding_failed" // EncodingError
  | "unknown"; // UnknownError, or an unrecognised DOMException

export interface RecordingError {
  kind: RecordingErrorKind;
  message: string;
  cause?: unknown;
  /** Raw DOMException name behind `kind`, kept for telemetry detail. */
  domErrorName?: string;
}

const MEDIA_RECORDER_ERROR_KIND_BY_DOM_NAME: Readonly<
  Record<string, RecordingErrorKind>
> = {
  InvalidStateError: "invalid_state",
  SecurityError: "security",
  InvalidModificationError: "track_changed",
  EncodingError: "encoding_failed",
};

function mapMediaRecorderErrorKind(
  domErrorName: string | undefined
): RecordingErrorKind {
  if (domErrorName == null) return "unknown";
  return MEDIA_RECORDER_ERROR_KIND_BY_DOM_NAME[domErrorName] ?? "unknown";
}

export interface UseMediaRecorderOptions {
  stream: MediaStream | null;
  /** Determines codec list and output filename prefix. */
  kind: "audio" | "video";
  bitrate?: number;
  /**
   * Interval in milliseconds at which `ondataavailable` fires.
   * @default 1000
   */
  timeslice?: number;
  maxFileSize: number;
  maxDurationSeconds: number;
  /**
   * Rewrite MP4 and WebM captures into a seekable container before handing them
   * to the upload path (see `remux.ts`).
   */
  remux?: boolean;
  /**
   * Milliseconds to wait for a remux before falling back to the raw blob.
   * @default 30000
   */
  remuxTimeoutMs?: number;
  /** Receives the stream the recorder bound to. */
  onRecordingStarted?: (
    stream: MediaStream,
    info: { mimeType: string }
  ) => void;
  /**
   * Fired when the recorder has stopped, before any remux. Callers measuring the
   * recording itself close their measurement here: `onMediaCaptured` can be up to
   * `remuxTimeoutMs` later, and the stream stays live until then.
   */
  onRecordingStopped?: () => void;
  onMediaCaptured?: (media: CapturedMedia, duration: number) => void;
  onRecordingError?: (error: RecordingError) => void;
  /** Fired just before a remux begins, with the raw capture size. */
  onRemuxStart?: (info: { inputBytes: number }) => void;
  /** Fired when a remux is skipped via fallback, so the caller can log it. */
  onRemuxFallback?: (error: unknown) => void;
  /** Fired when a recorder error sends the recording to the next codec. */
  onCodecFallback?: (info: { from: string; to: string }) => void;
}

export interface UseMediaRecorderReturn {
  /**
   * @param streamOverride Record this stream instead of the `stream` option,
   * for callers that pick the source at start time (see `use-webcam-capture`'s
   * fallback when the audio graph is not running).
   */
  startRecording: (streamOverride?: MediaStream | null) => void;
  stopRecording: () => void;
  isRecording: boolean;
  recordingDuration: number;
  /** True while a capture is being remuxed, between stop and upload. */
  isProcessing: boolean;
}

export const DEFAULT_REMUX_TIMEOUT_MS = 30_000;

/** MP4 and WebM record without a duration; mediabunny cannot output Ogg. */
function isRemuxableContainer(mimeType: string): boolean {
  return /^(audio|video)\/(mp4|webm)/.test(mimeType);
}

function teardownRecorder(recorder: MediaRecorder): void {
  recorder.ondataavailable = null;
  recorder.onstop = null;
  recorder.onerror = null;
}

/**
 * Low-level hook that wraps the `MediaRecorder` API for a given stream.
 *
 * Knows nothing about camera management or photo capture. Errors surface via
 * `onRecordingError` — the hook never calls `alert()`. The duration timer uses
 * a ref-based stop callback to avoid stale closures.
 *
 * A recording binds to the track instances present when `startRecording` calls
 * `recorder.start()`.
 */
export function useMediaRecorder({
  stream,
  kind,
  bitrate,
  timeslice = 1000,
  maxFileSize,
  maxDurationSeconds,
  remux = true,
  remuxTimeoutMs = DEFAULT_REMUX_TIMEOUT_MS,
  onRecordingStarted,
  onRecordingStopped,
  onMediaCaptured,
  onRecordingError,
  onRemuxStart,
  onRemuxFallback,
  onCodecFallback,
}: UseMediaRecorderOptions): UseMediaRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const recordingDurationRef = useRef(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  /** Guards against starting a new recording while onstop is still pending. */
  const isFinalizingRef = useRef(false);
  /**
   * Mime types whose encoder failed during this attempt chain.
   * `isTypeSupported` returning true does not promise the browser can encode.
   */
  const failedMimeTypesRef = useRef<Set<string>>(new Set());

  // Always-fresh refs so internal callbacks never capture stale values.
  const callbacksRef = useRef({
    onRecordingStarted,
    onRecordingStopped,
    onMediaCaptured,
    onRecordingError,
    onRemuxStart,
    onRemuxFallback,
    onCodecFallback,
  });
  useEffect(() => {
    callbacksRef.current = {
      onRecordingStarted,
      onRecordingStopped,
      onMediaCaptured,
      onRecordingError,
      onRemuxStart,
      onRemuxFallback,
      onCodecFallback,
    };
  });

  const remuxRef = useRef({ enabled: remux, timeoutMs: remuxTimeoutMs });
  useEffect(() => {
    remuxRef.current = { enabled: remux, timeoutMs: remuxTimeoutMs };
  }, [remux, remuxTimeoutMs]);

  const maxDurationRef = useRef(maxDurationSeconds);
  useEffect(() => {
    maxDurationRef.current = maxDurationSeconds;
  }, [maxDurationSeconds]);

  // Stable ref to stopRecording so the timer interval can call it without
  // capturing a stale function reference.
  const stopRecordingRef = useRef<() => void>(() => undefined);

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current == null ||
      mediaRecorderRef.current.state !== "recording"
    ) {
      return;
    }

    mediaRecorderRef.current.stop();
    isFinalizingRef.current = true;
    // isRecording flag and timer are cleared in onstop so all stop paths
    // (manual, size-exceeded, error) converge on the same teardown.
  }, []);

  // Keep the ref current so the timer closure always calls the latest version.
  stopRecordingRef.current = stopRecording;

  // Stable ref so recorder.onerror can retry with the latest attempt function.
  const startAttemptRef = useRef<(streamOverride?: MediaStream | null) => void>(
    () => undefined
  );

  const startAttempt = useCallback(
    (streamOverride?: MediaStream | null) => {
      const activeStream = streamOverride ?? stream;
      if (activeStream == null) return;

      // A codec already failed, so the caller was told the recording started
      // on the first attempt and must not be told again.
      const isRetry = failedMimeTypesRef.current.size > 0;

      if (isFinalizingRef.current) {
        console.error(
          "Cannot start a new recording while the previous one is finalising."
        );
        return;
      }

      // Tear down any existing recorder that is still active.
      if (
        mediaRecorderRef.current != null &&
        mediaRecorderRef.current.state === "recording"
      ) {
        teardownRecorder(mediaRecorderRef.current);
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }

      const codec = detectCodec(kind, failedMimeTypesRef.current);
      if (codec == null) {
        callbacksRef.current.onRecordingError?.({
          kind: "unsupported",
          message: `${kind === "audio" ? "Audio" : "Video"} recording is not supported in your browser.`,
        });
        setIsRecording(false);
        return;
      }

      chunksRef.current = [];
      let accumulatedSize = 0;

      try {
        const recorderOptions: MediaRecorderOptions = {
          mimeType: codec.mimeType,
        };
        if (bitrate != null) {
          if (kind === "audio") {
            recorderOptions.audioBitsPerSecond = bitrate;
          } else {
            recorderOptions.videoBitsPerSecond = bitrate;
          }
        }

        const recorder = new MediaRecorder(activeStream, recorderOptions);
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
          if (event.data.size === 0) return;

          accumulatedSize += event.data.size;
          chunksRef.current.push(event.data);

          // Stop as soon as we exceed the limit so the recorder doesn't keep
          // buffering. onstop will see blob.size > maxFileSize and fire the
          // error there
          if (
            accumulatedSize > maxFileSize &&
            mediaRecorderRef.current?.state === "recording"
          ) {
            mediaRecorderRef.current.stop();
            isFinalizingRef.current = true;
          }
        };

        recorder.onstop = () => {
          setIsRecording(false);
          if (timerRef.current != null) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          callbacksRef.current.onRecordingStopped?.();

          const blob = new Blob(chunksRef.current, { type: codec.mimeType });
          chunksRef.current = [];

          if (blob.size > maxFileSize) {
            callbacksRef.current.onRecordingError?.({
              kind: "size_exceeded",
              message: `Recording stopped: maximum file size of ${Math.round(maxFileSize / 1024 / 1024)} MB reached.`,
            });
            if (mediaRecorderRef.current != null) {
              teardownRecorder(mediaRecorderRef.current);
              mediaRecorderRef.current = null;
            }
            isFinalizingRef.current = false;
            return;
          }

          const emitCaptured = (output: Blob) => {
            const prefix = kind === "audio" ? "recording-audio" : "recording";
            const file = new File(
              [output],
              generateFilename(prefix, codec.extension),
              { type: codec.mimeType }
            );
            const url = URL.createObjectURL(output);

            callbacksRef.current.onMediaCaptured?.(
              { type: kind, url, file },
              recordingDurationRef.current
            );

            // Defer teardown so this onstop handler completes before the ref
            // is cleared — matches the existing `CLEANUP_DELAY_MS = 0` pattern.
            // isFinalizingRef stays set across the remux so a new recording
            // can't start mid-flatten.
            setTimeout(() => {
              if (mediaRecorderRef.current != null) {
                teardownRecorder(mediaRecorderRef.current);
                mediaRecorderRef.current = null;
              }
              isFinalizingRef.current = false;
            }, 0);
          };

          if (
            !remuxRef.current.enabled ||
            !isRemuxableContainer(codec.mimeType)
          ) {
            emitCaptured(blob);
            return;
          }

          // Rewrite the capture's container so it carries a real length before
          // it reaches the upload path. Best-effort: if remux fails or times
          // out, fall back to the raw blob.
          setIsProcessing(true);
          callbacksRef.current.onRemuxStart?.({ inputBytes: blob.size });
          let settled = false;
          function settle(output: Blob, fallbackError?: unknown) {
            if (settled) return;
            settled = true;
            setIsProcessing(false);
            if (fallbackError !== undefined) {
              callbacksRef.current.onRemuxFallback?.(fallbackError);
            }
            emitCaptured(output);
          }

          // Aborting on timeout cancels the in-flight conversion so it stops
          // burning CPU/memory once we've already fallen back to the raw blob.
          const abortController = new AbortController();
          const timeout = setTimeout(() => {
            abortController.abort();
            settle(blob, new Error("Remux timed out"));
          }, remuxRef.current.timeoutMs);
          import("./remux")
            .then(({ remuxToSeekableContainer }) =>
              remuxToSeekableContainer(blob, { signal: abortController.signal })
            )
            .then((seekable) => {
              clearTimeout(timeout);
              settle(seekable);
            })
            .catch((error: unknown) => {
              clearTimeout(timeout);
              settle(blob, error);
            });
        };

        recorder.onerror = (event) => {
          console.error("MediaRecorder error", event);
          // Native event carries a DOMException on `.error` (per spec/MDN);
          // lib.dom.d.ts still types this as plain ErrorEvent, hence the cast.
          const domErrorName = (event as { error?: { name?: string } }).error
            ?.name;
          const errorKind = mapMediaRecorderErrorKind(domErrorName);

          if (timerRef.current != null) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          if (mediaRecorderRef.current != null) {
            teardownRecorder(mediaRecorderRef.current);
            mediaRecorderRef.current = null;
          }
          // Read before the reset below, which is the only record that a stop
          // was already asked for and `onstop` had not run yet.
          const stopWasRequested = isFinalizingRef.current;
          isFinalizingRef.current = false;

          // A recorder that failed before emitting a chunk has produced nothing
          // to lose, so try the next codec instead of failing the recording.
          // The DOMException name is left out on purpose: Chromium reports
          // a dead encoder as UnknownError as often as EncodingError.
          if (
            chunksRef.current.length === 0 &&
            recordingDurationRef.current === 0 &&
            !stopWasRequested
          ) {
            failedMimeTypesRef.current.add(codec.mimeType);
            const nextCodec = detectCodec(kind, failedMimeTypesRef.current);
            if (nextCodec != null) {
              callbacksRef.current.onCodecFallback?.({
                from: codec.mimeType,
                to: nextCodec.mimeType,
              });
              startAttemptRef.current(activeStream);
              return;
            }
          }

          callbacksRef.current.onRecordingError?.({
            kind: errorKind,
            message: "An error occurred during recording.",
            cause: event,
            domErrorName,
          });
          setIsRecording(false);
        };

        recorder.start(timeslice);
        setIsRecording(true);
        recordingDurationRef.current = 0;
        setRecordingDuration(0);

        timerRef.current = setInterval(() => {
          setRecordingDuration((prev) => {
            const next = prev + 1;
            recordingDurationRef.current = next;
            if (next >= maxDurationRef.current) {
              stopRecordingRef.current();
            }
            return next;
          });
        }, 1000);

        if (!isRetry) {
          callbacksRef.current.onRecordingStarted?.(activeStream, {
            mimeType: codec.mimeType,
          });
        }
      } catch (error) {
        console.error("Failed to start MediaRecorder", error);
        // Per spec, start() throws InvalidStateError synchronously when the
        // recorder isn't inactive — that lands here, not in onerror.
        // https://www.w3.org/TR/mediastream-recording/#dom-mediarecorder-start (15.5)
        const domErrorName =
          error instanceof DOMException ? error.name : undefined;
        callbacksRef.current.onRecordingError?.({
          kind: mapMediaRecorderErrorKind(domErrorName),
          message: `Failed to start recording. Your browser may not support ${kind} recording.`,
          cause: error,
          domErrorName,
        });
        setIsRecording(false);
      }
    },
    [stream, kind, bitrate, timeslice, maxFileSize]
  );
  startAttemptRef.current = startAttempt;

  const startRecording = useCallback(
    (streamOverride?: MediaStream | null) => {
      failedMimeTypesRef.current = new Set();
      startAttempt(streamOverride);
    },
    [startAttempt]
  );

  // Cleanup on unmount — stop any active recorder silently.
  useEffect(() => {
    return () => {
      if (timerRef.current != null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (mediaRecorderRef.current != null) {
        teardownRecorder(mediaRecorderRef.current);
        if (mediaRecorderRef.current.state !== "inactive") {
          try {
            mediaRecorderRef.current.stop();
          } catch {
            // already stopped
          }
        }
        mediaRecorderRef.current = null;
      }
      chunksRef.current = [];
    };
  }, []);

  return {
    startRecording,
    stopRecording,
    isRecording,
    recordingDuration,
    isProcessing,
  };
}
